import { CourseStatus, type CourseStatusValue } from './course-satellite';

export const CourseVisibility = {
  PUBLIC: 'public',
  UNLISTED: 'unlisted',
  CIRCLE_ONLY: 'circle_only',
} as const;

export type CourseVisibilityValue = (typeof CourseVisibility)[keyof typeof CourseVisibility];

export const CourseLevel = {
  BEGINNER: 'beginner',
  INTERMEDIATE: 'intermediate',
  ADVANCED: 'advanced',
  ALL: 'all',
} as const;

export type CourseLevelValue = (typeof CourseLevel)[keyof typeof CourseLevel];

export const CoursePriceType = {
  FREE: 'free',
  ONE_TIME: 'one_time',
} as const;

export type CoursePriceTypeValue = (typeof CoursePriceType)[keyof typeof CoursePriceType];

export const CourseEnrollmentAccessSource = {
  FREE_ENROLLMENT: 'free_enrollment',
  MEMBERSHIP_PERK: 'membership_perk',
  MEMBER_PRICE_PURCHASE: 'member_price_purchase',
  STANDALONE_PURCHASE: 'standalone_purchase',
  MANUAL_GRANT: 'manual_grant',
  SERVICE_PACKAGE: 'service_package',
} as const;

export type CourseEnrollmentAccessSourceValue =
  (typeof CourseEnrollmentAccessSource)[keyof typeof CourseEnrollmentAccessSource];

export type CoursePublishCheckInput = {
  title: string;
  coverUrl?: string | null;
  coverKey?: string | null;
  slug?: string | null;
  priceType: string;
  priceAmountCents?: number | null;
  publishedLessonCount: number;
  videoLessonsBlocked?: number;
  refundPolicyText?: string | null;
  connectReady?: boolean;
};

export type CoursePublishCheckItem = {
  key: string;
  label: string;
  passed: boolean;
  message?: string;
};

export function evaluateCoursePublishChecklist(input: CoursePublishCheckInput): {
  ready: boolean;
  items: CoursePublishCheckItem[];
} {
  const hasCover = Boolean(input.coverUrl?.trim() || input.coverKey?.trim());
  const hasTitle = input.title.trim().length >= 3;
  const hasSlug = Boolean(input.slug?.trim());
  const hasPublishedLesson = input.publishedLessonCount > 0;
  const videoReady = (input.videoLessonsBlocked ?? 0) === 0;
  const pricingOk =
    input.priceType === CoursePriceType.FREE ||
    (input.priceAmountCents != null && input.priceAmountCents > 0);
  const refundPolicyOk =
    input.priceType === CoursePriceType.FREE || Boolean(input.refundPolicyText?.trim());
  const connectOk =
    input.priceType === CoursePriceType.FREE || input.connectReady !== false;

  const items: CoursePublishCheckItem[] = [
    {
      key: 'title',
      label: 'Title (min 3 characters)',
      passed: hasTitle,
      message: hasTitle ? undefined : 'Add a course title.',
    },
    {
      key: 'cover',
      label: 'Cover image',
      passed: hasCover,
      message: hasCover ? undefined : 'Upload a cover image.',
    },
    {
      key: 'lessons',
      label: 'At least one published lesson',
      passed: hasPublishedLesson,
      message: hasPublishedLesson ? undefined : 'Publish at least one lesson.',
    },
    {
      key: 'video',
      label: 'Video lessons processed',
      passed: videoReady,
      message: videoReady
        ? undefined
        : 'Wait for video uploads to finish processing or fix failed videos.',
    },
    {
      key: 'pricing',
      label: 'Pricing configured',
      passed: pricingOk,
      message: pricingOk ? undefined : 'Set a price or mark the course as free.',
    },
    {
      key: 'refund_policy',
      label: 'Refund policy (paid courses)',
      passed: refundPolicyOk,
      message: refundPolicyOk ? undefined : 'Add a refund policy for paid courses.',
    },
    {
      key: 'connect',
      label: 'Stripe Connect ready (paid courses)',
      passed: connectOk,
      message: connectOk ? undefined : 'Complete Connect onboarding before publishing a paid course.',
    },
    {
      key: 'slug',
      label: 'Public slug',
      passed: hasSlug,
      message: hasSlug ? undefined : 'Slug will be generated on publish.',
    },
  ];

  return {
    ready: items.every((item) => item.passed || item.key === 'slug'),
    items,
  };
}

export function isCourseDiscoverable(params: {
  status: string;
  visibility: string;
}): boolean {
  return (
    params.status === CourseStatus.PUBLISHED &&
    params.visibility === CourseVisibility.PUBLIC
  );
}

export function isCoursePublicSalesListed(params: {
  status: string;
  visibility: string;
}): boolean {
  return (
    params.status === CourseStatus.PUBLISHED &&
    (params.visibility === CourseVisibility.PUBLIC ||
      params.visibility === CourseVisibility.UNLISTED)
  );
}

export function isCourseCircleOnly(params: { visibility: string }): boolean {
  return params.visibility === CourseVisibility.CIRCLE_ONLY;
}

export function canRevokeCourseEnrollmentAccess(params: {
  accessSource: string;
  hasActivePurchase: boolean;
}): { allowed: boolean; reason?: string } {
  if (!params.hasActivePurchase) {
    return { allowed: true };
  }

  if (
    params.accessSource === CourseEnrollmentAccessSource.STANDALONE_PURCHASE ||
    params.accessSource === CourseEnrollmentAccessSource.MEMBER_PRICE_PURCHASE
  ) {
    return {
      allowed: false,
      reason: 'Refund the purchase before revoking paid enrollments',
    };
  }

  return { allowed: true };
}

export function isPublishedCourseStatusValue(status: string): status is CourseStatusValue {
  return status === CourseStatus.PUBLISHED;
}
